import { useEffect, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import OtpInput from "../components/OtpInput";
import LogoMark from "../components/LogoMark";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Verify() {
  const location = useLocation();
  const navigate = useNavigate();
  const { completePersonRegistration, completeOrgRegistration } = useAuth();
  const state = location.state || {};

  const [personOtp, setPersonOtp] = useState("");
  const [orgOtp, setOrgOtp] = useState("");
  const [contactOtp, setContactOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [devOrgOtp, setDevOrgOtp] = useState(state.dev_org_otp);
  const [devContactOtp, setDevContactOtp] = useState(state.dev_contact_otp);
  const [devPersonOtp, setDevPersonOtp] = useState(state.dev_otp);

  useEffect(() => {
    if (!state.session_id) navigate("/", { replace: true });
  }, [state.session_id, navigate]);

  if (!state.session_id) return <Navigate to="/" replace />;

  const resend = async (which = "primary") => {
    setError("");
    try {
      const { data } = await api.post("/auth/resend-otp", { session_id: state.session_id, which });
      if (state.kind === "person") setDevPersonOtp(data.dev_otp);
      else if (which === "contact") setDevContactOtp(data.dev_otp);
      else setDevOrgOtp(data.dev_otp);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const submitPerson = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/verify-person", {
        session_id: state.session_id,
        otp: personOtp,
      });
      completePersonRegistration(data);
      navigate("/home", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  const submitOrg = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/verify-org", {
        session_id: state.session_id,
        organization_otp: orgOtp,
        contact_person_otp: contactOtp,
        password,
        confirm_password: confirmPassword,
      });
      completeOrgRegistration(data);
      navigate("/home", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start py-12 px-4 bg-[#050d1a]">
      <div className="csc-grid-bg absolute inset-0 -z-10 opacity-30" />
      <div className="mb-8"><LogoMark size={44} withText /></div>

      {state.kind === "person" ? (
        <form
          onSubmit={submitPerson}
          className="csc-card p-8 lg:p-10 max-w-md w-full space-y-6"
          data-testid="verify-person-form"
        >
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">Verify Email</div>
            <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Enter your 6-digit code</h1>
            <p className="text-sm text-[#94a3b8] mt-2">
              Sent to <span className="text-white font-mono-tech">{state.email}</span>
            </p>
          </div>

          {devPersonOtp && (
            <div className="font-mono-tech text-xs px-3 py-2 rounded border border-[#39ff14]/40 bg-[#39ff14]/5 text-[#39ff14]" data-testid="dev-otp-banner">
              [DEV / Mock Email Mode] OTP: <strong className="tracking-[0.35em] text-base">{devPersonOtp}</strong>
            </div>
          )}

          <OtpInput value={personOtp} onChange={setPersonOtp} data-testid="person-otp" />

          {error && <div className="text-sm text-red-400 text-center" data-testid="verify-error">{error}</div>}

          <button type="submit" disabled={submitting || personOtp.length !== 6} className="csc-btn-secondary w-full" data-testid="verify-person-submit">
            {submitting ? "Verifying…" : "Verify & Enter"}
          </button>

          <button type="button" onClick={() => resend("primary")} className="block w-full text-center text-xs font-mono-tech text-[#94a3b8] hover:text-[#00aaff]" data-testid="resend-person-otp">
            Resend code →
          </button>
        </form>
      ) : (
        <form
          onSubmit={submitOrg}
          className="csc-card p-8 lg:p-10 max-w-xl w-full space-y-6"
          data-testid="verify-org-form"
        >
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#00aaff]">Verify Organization</div>
            <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Two verifications + set password</h1>
            <p className="text-sm text-[#94a3b8] mt-2">
              We sent codes to <span className="text-white">{state.organization_email}</span> &{" "}
              <span className="text-white">{state.contact_person_email}</span>.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="csc-label !mb-0">Organization Email OTP</label>
                {devOrgOtp && (
                  <span className="font-mono-tech text-[11px] text-[#00aaff]" data-testid="dev-org-otp">
                    [DEV] {devOrgOtp}
                  </span>
                )}
              </div>
              <OtpInput value={orgOtp} onChange={setOrgOtp} data-testid="org-otp" />
              <button type="button" onClick={() => resend("primary")} className="mt-2 text-[11px] font-mono-tech text-[#94a3b8] hover:text-[#00aaff]" data-testid="resend-org-otp">
                Resend organization code
              </button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="csc-label !mb-0">Contact Person Email OTP</label>
                {devContactOtp && (
                  <span className="font-mono-tech text-[11px] text-[#39ff14]" data-testid="dev-contact-otp">
                    [DEV] {devContactOtp}
                  </span>
                )}
              </div>
              <OtpInput value={contactOtp} onChange={setContactOtp} data-testid="contact-otp" />
              <button type="button" onClick={() => resend("contact")} className="mt-2 text-[11px] font-mono-tech text-[#94a3b8] hover:text-[#39ff14]" data-testid="resend-contact-otp">
                Resend contact person code
              </button>
            </div>
          </div>

          <div className="pt-2 border-t border-[rgba(0,170,255,0.18)]">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-3">
              Set password for Org Admin login
            </p>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="csc-label">Password (12+ chars, upper, number, special)</label>
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="csc-input" data-testid="org-set-password" />
              </div>
              <div>
                <label className="csc-label">Confirm Password</label>
                <input required type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="csc-input" data-testid="org-confirm-password" />
              </div>
            </div>
          </div>

          {error && <div className="text-sm text-red-400" data-testid="verify-org-error">{error}</div>}

          <button
            type="submit"
            disabled={submitting || orgOtp.length !== 6 || contactOtp.length !== 6}
            className="csc-btn-primary w-full"
            data-testid="verify-org-submit"
          >
            {submitting ? "Verifying…" : "Verify & Launch Account"}
          </button>
        </form>
      )}
    </div>
  );
}
